import { defineStore } from 'pinia'
import { api, redirectToLogin, SessionExpiredError, type Plan } from '../services/api'
import { useSessionStore } from './session'
import { FILTERS } from '../constants/filters'
import { PERMISSIONS, PLANS_QUERY_DEFAULTS } from '../constants/plansQuery'

type PlansOptions = NonNullable<Parameters<typeof api.getPlans>[1]>

// Module-level abort controller (not in state - AbortController isn't serializable)
let fetchAbortController: AbortController | null = null

const SIGN_IN_STALL_MS = 15_000

const supersedes = (controller: AbortController) => fetchAbortController !== null && fetchAbortController !== controller

const noResults = () => ({
  plans: [] as Plan[],
  hasMore: false,
  lastEvaluatedId: undefined,
  lastEvaluatedSort: undefined,
})

interface LoadingQuery {
  filter: string
  search: string
}

interface PlansState {
  plans: Plan[]
  // Server-computed, pre-authorization-filtering (see api.ts's PlansResponse) —
  // not derived from `plans`, since the last VIEWABLE row here isn't
  // necessarily the last RAW row the server paged from.
  hasMore: boolean
  lastEvaluatedId?: string
  lastEvaluatedSort?: string
  isLoading: boolean
  isAppending: boolean
  lastAppendWasEmpty: boolean
  error: string | null
  loadingQuery: LoadingQuery | null
}

export const usePlansStore = defineStore('plans', {
  state: (): PlansState => ({
    plans: [],
    hasMore: false,
    lastEvaluatedId: undefined,
    lastEvaluatedSort: undefined,
    isLoading: false,
    isAppending: false,
    lastAppendWasEmpty: false,
    error: null,
    loadingQuery: null,
  }),

  getters: {
    loadingDescription(state): string {
      const filter = state.loadingQuery?.filter ?? PLANS_QUERY_DEFAULTS.FILTER
      const search = state.loadingQuery?.search ?? ''
      const filterLabel =
        filter !== PLANS_QUERY_DEFAULTS.FILTER ? FILTERS.find(f => f.value === filter)?.label : undefined

      if (search && filterLabel) return `Searching ${filterLabel} plans for "${search}"…`
      if (search) return `Searching plans for "${search}"…`
      if (filterLabel) return `Loading ${filterLabel} plans…`
      return 'Loading plans…'
    },

    hasMoreToLoad(state): boolean {
      return state.hasMore && !state.isLoading && Boolean(state.lastEvaluatedId)
    },

    canLoadMore(state): boolean {
      return this.hasMoreToLoad && !state.isAppending
    },
  },

  actions: {
    async fetchPlans(filter: string, queryParams: PlansOptions) {
      fetchAbortController?.abort('a newer plans request superseded this one')
      const currentController = new AbortController()
      fetchAbortController = currentController

      this.isLoading = true
      this.isAppending = false
      this.lastAppendWasEmpty = false
      this.error = null
      // Written unconditionally here, not after the fetch resolves: this request
      // owns the controller at this point, so a superseded request's later
      // success/catch can never reach this line to revert it.
      this.loadingQuery = { filter, search: queryParams.search ?? '' }
      let signingBackIn = false

      try {
        const sessionStore = useSessionStore()
        if (!sessionStore.isAuthenticated) {
          redirectToLogin()
          throw new SessionExpiredError()
        }
        if (!sessionStore.hasPermission(PERMISSIONS.VIEW_PLANS)) {
          throw new Error("You don't have permission to view payment plans. (Forbidden)")
        }

        const response = await api.getPlans(filter, { ...queryParams, signal: currentController.signal })

        if (fetchAbortController !== currentController) return

        this.plans = response.plans
        this.hasMore = response.hasMore
        this.lastEvaluatedId = response.lastEvaluatedId
        this.lastEvaluatedSort = response.lastEvaluatedSort
      } catch (err) {
        if (currentController.signal.aborted) return
        if (err instanceof SessionExpiredError) {
          signingBackIn = true
          setTimeout(() => {
            if (supersedes(currentController)) return
            this.$patch({ error: err.message, isLoading: false, ...noResults() })
          }, SIGN_IN_STALL_MS)
          return
        }

        this.$patch({ error: err instanceof Error ? err.message : 'Failed to fetch plans', ...noResults() })
      } finally {
        if (!signingBackIn && fetchAbortController === currentController) {
          this.isLoading = false
        }
      }
    },

    // Both lines matter: abort rejects an outstanding request, and disowning the
    // controller discards one that already resolved into the microtask queue,
    // where abort can no longer reach it.
    resetCursor() {
      fetchAbortController?.abort('a plans query change discarded this request')
      fetchAbortController = null
      this.lastAppendWasEmpty = false
      this.hasMore = false
      this.lastEvaluatedId = undefined
      this.lastEvaluatedSort = undefined
    },

    async loadMore(filter: string, queryParams: PlansOptions) {
      if (!this.canLoadMore) return

      fetchAbortController?.abort('a newer plans request superseded this one')
      const currentController = new AbortController()
      fetchAbortController = currentController

      this.isAppending = true
      this.lastAppendWasEmpty = false
      this.error = null
      let signingBackIn = false

      try {
        const response = await api.getPlans(filter, {
          ...queryParams,
          last_evaluated_id: this.lastEvaluatedId,
          last_evaluated_sort: this.lastEvaluatedSort,
          signal: currentController.signal,
        })

        if (fetchAbortController !== currentController) return

        this.lastAppendWasEmpty = response.plans.length === 0
        this.plans = [...this.plans, ...response.plans]
        this.hasMore = response.hasMore
        this.lastEvaluatedId = response.lastEvaluatedId
        this.lastEvaluatedSort = response.lastEvaluatedSort
      } catch (err) {
        if (currentController.signal.aborted) return
        if (err instanceof SessionExpiredError) {
          signingBackIn = true
          setTimeout(() => {
            if (supersedes(currentController)) return
            this.$patch({ error: err.message, isAppending: false })
          }, SIGN_IN_STALL_MS)
          return
        }

        this.error = err instanceof Error ? err.message : 'Failed to load more plans'
      } finally {
        // Only one append can be in flight at a time (the canLoadMore guard
        // above), so unlike fetchPlans this doesn't need to check controller
        // ownership before clearing its own flag.
        if (!signingBackIn) this.isAppending = false
      }
    },
  },
})
